import React, { useState } from "react";
import { Loader2, Check, Quote } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";

const RELATIONSHIPS = [
    "Hotel owner",
    "Asset manager / Investor",
    "General Manager / Operator",
    "Former colleague",
    "Industry contact",
    "Supplier / partner",
    "Other",
];

export default function RecommendPage() {
    useSEO({
        title: "Share a Recommendation | Optimum Hospitality Solutions",
        description:
            "If you have worked with Sidney Adams as an owner, asset manager, operator, colleague or industry contact, share a short recommendation that may appear on the Optimum Hospitality Solutions website.",
        path: "/recommend",
    });

    const [form, setForm] = useState({
        name: "",
        email: "",
        role: "",
        organisation: "",
        relationship: "",
        recommendation: "",
    });
    const [permission, setPermission] = useState(false);
    const [allowName, setAllowName] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.role || !form.relationship || !form.recommendation) {
            toast.error("Please complete the required fields.");
            return;
        }
        if (form.recommendation.length < 40) {
            toast.error("Please write a recommendation of at least 40 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/recommendations", {
                ...form,
                permission_to_publish: permission,
                allow_name: allowName,
            });
            setSubmitted(true);
            toast.success("Recommendation received. Thank you.");
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again or email us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div data-testid="recommend-success" className="min-h-[60vh]">
                <div className="mx-auto max-w-3xl px-6 lg:px-12 py-24 text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-champagne/20 text-brand-champagne mx-auto">
                        <Check size={28} strokeWidth={1.5} />
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-8 text-brand-navy">
                        Thank you.
                    </h1>
                    <p className="mt-5 text-brand-ink/75 leading-relaxed">
                        Your recommendation has been received. Sidney will review it personally and, if you have given permission to publish, will be in touch to confirm the wording and attribution before anything appears on the website.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="recommend-page">
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                        Share a recommendation
                    </span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        A few words<br/>that mean a lot.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                        If you have worked alongside Sidney Adams as a hotel owner, asset manager, operator, colleague or industry contact, please share a short recommendation. With your permission, selected recommendations are published on the Optimum Hospitality Solutions website.
                    </p>
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-4">
                        <div className="border border-brand-border p-8 bg-white">
                            <Quote className="text-brand-champagne" size={28} strokeWidth={1.25} />
                            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                Guidance
                            </div>
                            <ul className="mt-5 space-y-4 text-sm text-brand-ink/80 leading-relaxed">
                                <li className="flex gap-3">
                                    <span className="text-brand-champagne">01</span>
                                    <span>Two to four sentences is plenty.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-brand-champagne">02</span>
                                    <span>Honest reflection is more valuable than superlatives.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-brand-champagne">03</span>
                                    <span>Mention a specific situation or outcome if you can.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-brand-champagne">04</span>
                                    <span>Anonymous submissions are welcome.</span>
                                </li>
                            </ul>
                            <div className="mt-8 pt-6 border-t border-brand-border text-xs text-brand-ink/60 leading-relaxed">
                                Nothing is published without your explicit permission. Sidney will be in touch to confirm wording and attribution before anything goes on the site.
                            </div>
                        </div>
                    </aside>

                    <form
                        onSubmit={submit}
                        data-testid="recommend-form"
                        className="lg:col-span-8 border border-brand-border p-8 lg:p-12 bg-white space-y-7"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <Label htmlFor="r-name" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Full name *
                                </Label>
                                <Input
                                    id="r-name"
                                    data-testid="recommend-input-name"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="r-email" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Email *
                                </Label>
                                <Input
                                    id="r-email"
                                    type="email"
                                    data-testid="recommend-input-email"
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="r-role" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Your role *
                                </Label>
                                <Input
                                    id="r-role"
                                    data-testid="recommend-input-role"
                                    placeholder="e.g. General Manager"
                                    value={form.role}
                                    onChange={(e) => set("role", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="r-org" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Organisation (optional)
                                </Label>
                                <Input
                                    id="r-org"
                                    data-testid="recommend-input-organisation"
                                    placeholder="e.g. Property or group name"
                                    value={form.organisation}
                                    onChange={(e) => set("organisation", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                Relationship with Sidney *
                            </Label>
                            <Select
                                value={form.relationship}
                                onValueChange={(v) => set("relationship", v)}
                            >
                                <SelectTrigger
                                    data-testid="recommend-select-relationship"
                                    className="mt-2 rounded-none border-brand-border focus:ring-brand-navy"
                                >
                                    <SelectValue placeholder="How do you know Sidney?" />
                                </SelectTrigger>
                                <SelectContent>
                                    {RELATIONSHIPS.map((r) => (
                                        <SelectItem
                                            key={r}
                                            value={r}
                                            data-testid={`recommend-relationship-${r.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "")}`}
                                        >
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="r-rec" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                Your recommendation *
                            </Label>
                            <Textarea
                                id="r-rec"
                                data-testid="recommend-input-text"
                                value={form.recommendation}
                                onChange={(e) => set("recommendation", e.target.value)}
                                rows={6}
                                placeholder="Two to four sentences. Honest reflection is more valuable than superlatives."
                                className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                required
                            />
                            <div className="mt-2 text-xs text-brand-ink/55">
                                {form.recommendation.length} characters
                            </div>
                        </div>

                        <div className="border-t border-brand-border pt-6 space-y-4">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                Permissions
                            </div>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <Checkbox
                                    checked={permission}
                                    onCheckedChange={(v) => setPermission(Boolean(v))}
                                    data-testid="recommend-permission-publish"
                                    className="mt-1"
                                />
                                <span className="text-sm text-brand-ink/80 leading-relaxed">
                                    I am happy for this recommendation to be considered for publication on the Optimum Hospitality Solutions website. Sidney will confirm wording with me before anything is published.
                                </span>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <Checkbox
                                    checked={allowName}
                                    onCheckedChange={(v) => setAllowName(Boolean(v))}
                                    data-testid="recommend-permission-name"
                                    className="mt-1"
                                />
                                <span className="text-sm text-brand-ink/80 leading-relaxed">
                                    I am happy for my name and role to be shown alongside the recommendation. (If unchecked, attribution will be anonymised by role only, for example &ldquo;General Manager, Independent Hotel&rdquo;.)
                                </span>
                            </label>
                        </div>

                        <Button
                            type="submit"
                            data-testid="recommend-submit-button"
                            disabled={submitting}
                            className="rounded-none bg-brand-navy text-brand-stone hover:bg-brand-hover px-8 py-6 text-sm tracking-wide"
                        >
                            {submitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending...
                                </span>
                            ) : (
                                "Submit recommendation"
                            )}
                        </Button>
                    </form>
                </div>
            </section>
        </div>
    );
}
